import React, { useState } from 'react';
import { theme } from '../../styles/theme';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

/** Themed text input with label, error state, and icon support. */
export const Input: React.FC<InputProps> = ({
  label,
  error,
  helperText,
  leftIcon,
  rightIcon,
  style,
  ...props
}) => {
  const [isFocused, setIsFocused] = useState(false);

  return (
    <div style={{ width: '100%' }}>
      {label && (
        <label
          style={{
            display: 'block',
            fontSize: theme.fontSizes.sm,
            fontWeight: 500,
            color: theme.colors.textSecondary,
            marginBottom: theme.spacing.xs,
          }}
        >
          {label}
        </label>
      )}

      <div
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {leftIcon && (
          <div
            style={{
              position: 'absolute',
              left: theme.spacing.md,
              color: theme.colors.textMuted,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {leftIcon}
          </div>
        )}

        <input
          {...props}
          onFocus={(e) => {
            setIsFocused(true);
            props.onFocus?.(e);
          }}
          onBlur={(e) => {
            setIsFocused(false);
            props.onBlur?.(e);
          }}
          style={{
            width: '100%',
            padding: theme.spacing.md,
            paddingLeft: leftIcon ? theme.spacing['2xl'] : theme.spacing.md,
            paddingRight: rightIcon ? theme.spacing['2xl'] : theme.spacing.md,
            background: theme.colors.bgTertiary,
            border: `1px solid ${
              error
                ? theme.colors.error
                : isFocused
                ? theme.colors.primary
                : theme.colors.border
            }`,
            borderRadius: theme.borderRadius.md,
            color: theme.colors.textPrimary,
            fontSize: theme.fontSizes.sm,
            outline: 'none',
            transition: `all ${theme.transitions.fast}`,
            boxShadow: isFocused ? `0 0 0 3px rgba(255, 77, 0, 0.1)` : 'none',
            ...style,
          }}
        />

        {rightIcon && (
          <div
            style={{
              position: 'absolute',
              right: theme.spacing.md,
              color: theme.colors.textMuted,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {rightIcon}
          </div>
        )}
      </div>

      {(error || helperText) && (
        <p
          style={{
            fontSize: theme.fontSizes.xs,
            color: error ? theme.colors.error : theme.colors.textMuted,
            margin: 0,
            marginTop: theme.spacing.xs,
          }}
        >
          {error || helperText}
        </p>
      )}
    </div>
  );
};

export default Input;
